import { useMemo, useState, useEffect } from "react";
import {
  cardMutedTextClass,
  cardSurfaceClass,
  cardTitleTextClass,
  compactInputClass,
  ghostBtnClass,
  insetSurfaceCardClass,
  secondarySurfaceClass,
} from "@/components/common/surfaceClasses";
import { buildRoleplayTrainingCatalogTiles } from "@/components/training-author/buildRoleplayTrainingCatalogTiles";
import { FIREFLY_TARGET_ATTR } from "@/lib/firefly/fireflyTargetConvention";
import { RoleplayOrgSettingsCollapsible } from "@/components/training-author/RoleplayOrgSettingsCollapsible";
import { RoleplayScenarioEditorPanel } from "@/components/training-author/RoleplayScenarioEditorPanel";
import { useRoleplayTrainingSection } from "@/components/training-author/useRoleplayTrainingSection";

const SWITCH_CONFIRM =
  "Switch to another scenario in the editor? You can reopen this one afterward; your edits stay in the list until you save or reload.";

export function RoleplayTrainingSection({ organizationId }: { organizationId: string }) {
  const vm = useRoleplayTrainingSection(organizationId);
  const [openRowIndex, setOpenRowIndex] = useState<number | null>(null);

  const catalogTiles = useMemo(
    () => buildRoleplayTrainingCatalogTiles(vm.rows, vm.mergedPreview),
    [vm.rows, vm.mergedPreview],
  );

  useEffect(() => {
    if (openRowIndex === null) return;
    if (openRowIndex < 0 || openRowIndex >= vm.rows.length) setOpenRowIndex(null);
  }, [vm.rows, openRowIndex]);

  function trySelectOrgRowIndex(nextIndex: number) {
    if (openRowIndex !== null && openRowIndex !== nextIndex) {
      if (!window.confirm(SWITCH_CONFIRM)) return;
    }
    setOpenRowIndex(nextIndex);
  }

  function handleNewScenario() {
    const nextIndex = vm.rows.length;
    vm.addRow();
    setOpenRowIndex(nextIndex);
  }

  function handleRemoveRowAt(index: number) {
    vm.removeRowAt(index);
    setOpenRowIndex((oi) => {
      if (oi === null) return null;
      if (oi === index) return null;
      if (oi > index) return oi - 1;
      return oi;
    });
  }

  async function handleDeleteRow(id: string) {
    const cur = openRowIndex !== null ? vm.rows[openRowIndex] : null;
    await vm.deleteRow(id);
    if (cur?.id === id) setOpenRowIndex(null);
  }

  const openRow = openRowIndex !== null ? vm.rows[openRowIndex] : null;

  return (
    <section className="space-y-4">
      {vm.msg ? (
        <div
          className={`text-sm rounded-lg px-3 py-2 border ${
            vm.msg.ok ? "bg-emerald-50 border-emerald-200 text-emerald-800 dark:bg-emerald-950/35 dark:border-emerald-800 dark:text-emerald-200" : "bg-red-50 border-red-200 text-red-700 dark:bg-red-950/35 dark:border-red-800 dark:text-red-300"
          }`}
        >
          {vm.msg.text}
        </div>
      ) : null}

      <RoleplayOrgSettingsCollapsible
        manufacturerOptions={vm.manufacturerOptions}
        useMfgScenarios={vm.useMfgScenarios}
        setUseMfgScenarios={vm.setUseMfgScenarios}
        mfgSourceId={vm.mfgSourceId}
        setMfgSourceId={vm.setMfgSourceId}
        freezeKbDigest={vm.freezeKbDigest}
        setFreezeKbDigest={vm.setFreezeKbDigest}
        saving={vm.saving}
        saveOrgFlags={vm.saveOrgFlags}
      />

      <div className={`${insetSurfaceCardClass} space-y-2`}>
        <div className={`text-sm font-semibold ${cardTitleTextClass}`}>Suggest scenarios from KB</div>
        <p className={`text-[11px] ${cardMutedTextClass}`}>
          One KB pull + one model call. Drafts import as <strong>inactive</strong> scenarios for your org until you publish them.
        </p>
        <input
          className={`w-full text-sm ${compactInputClass}`}
          placeholder="Topic or training focus (e.g. storm shelters, rent-to-own objections)"
          value={vm.topicPrompt}
          onChange={(e) => vm.setTopicPrompt(e.target.value)}
        />
        <button
          type="button"
          disabled={vm.busy === "suggest" || vm.saving}
          onClick={() => void vm.runSuggestScenarios()}
          className="px-3 py-2 rounded-lg bg-gray-900 text-white text-xs disabled:opacity-50"
        >
          {vm.busy === "suggest" ? "Suggesting…" : "Suggest & import drafts"}
        </button>
      </div>

      <div className={`rounded-lg border p-3 space-y-3 ${secondarySurfaceClass}`}>
        <div className="flex flex-wrap items-center justify-between gap-2">
          <div>
            <div className={`text-sm font-semibold ${cardTitleTextClass}`}>Trainee catalog &amp; scenario editor</div>
            <p className={`text-xs mt-0.5 leading-relaxed ${cardMutedTextClass}`}>
              Click a workspace scenario to edit it. Manufacturer and platform items are read-only here — edit manufacturer scenarios on the manufacturer org.{" "}
              <strong>Publish</strong> makes a scenario live for the Roleplay tab; <strong>Save changes</strong> persists fields; <strong>Delete</strong> removes an org row.
            </p>
          </div>
          <div className="flex gap-2 shrink-0">
            <button
              type="button"
              className={`text-xs px-2 py-1 rounded ${ghostBtnClass}`}
              onClick={() => void vm.reloadAll()}
              disabled={vm.saving}
            >
              Reload
            </button>
            <button
              type="button"
              {...{ [FIREFLY_TARGET_ATTR]: "ta-new-scenario" }}
              className="text-xs px-2 py-1 rounded bg-indigo-600 text-white"
              onClick={handleNewScenario}
              disabled={vm.saving}
            >
              New scenario
            </button>
          </div>
        </div>

        {vm.rows.length === 0 && catalogTiles.every((t) => t.kind === "readonly") ? (
          <div className={`rounded-xl border border-dashed border-gray-300 px-4 py-6 text-center text-sm dark:border-zinc-600 ${cardSurfaceClass} ${cardMutedTextClass}`}>
            No org scenarios yet. Click <strong>New scenario</strong> or run <strong>Suggest &amp; import drafts</strong>, then pick a tile below and{" "}
            <strong>Publish</strong> when ready.
          </div>
        ) : null}

        <div
          {...{ [FIREFLY_TARGET_ATTR]: "ta-catalog-grid" }}
          className="flex flex-wrap gap-2 max-h-48 overflow-y-auto p-1"
        >
          {catalogTiles.map((tile) => {
            if (tile.kind === "readonly") {
              return (
                <div
                  key={`ro-${tile.scenarioKey}`}
                  title="Not editable on this org — open the manufacturer workspace or use platform defaults as documented."
                  className={`min-w-[140px] max-w-[220px] rounded-lg border border-dashed border-gray-300 px-2 py-2 text-left cursor-not-allowed opacity-80 dark:border-zinc-600 ${cardSurfaceClass}`}
                >
                  <div className={`text-xs font-semibold truncate ${cardTitleTextClass}`}>{tile.label}</div>
                  <div className={`text-[10px] mt-0.5 ${cardMutedTextClass}`}>
                    {tile.source === "platform_example" ? "Platform example" : "Manufacturer"}
                  </div>
                </div>
              );
            }
            const selected = openRowIndex === tile.rowIndex;
            return (
              <button
                key={`org-${tile.scenarioKey}-${tile.rowIndex}`}
                type="button"
                onClick={() => trySelectOrgRowIndex(tile.rowIndex)}
                className={
                  "min-w-[140px] max-w-[220px] rounded-lg border px-2 py-2 text-left transition-shadow " +
                  (selected
                    ? "border-indigo-500 ring-2 ring-indigo-400 shadow dark:ring-indigo-600 " + cardSurfaceClass
                    : "border-gray-200 hover:border-gray-300 hover:shadow-sm dark:border-zinc-700 dark:hover:border-zinc-500 " + cardSurfaceClass)
                }
              >
                <div className={`text-xs font-semibold truncate ${cardTitleTextClass}`}>{tile.label}</div>
                <div className={`text-[10px] font-mono truncate mt-0.5 ${cardMutedTextClass}`}>{tile.scenarioKey}</div>
                <div className="flex flex-wrap gap-1 mt-1.5">
                  <span
                    className={
                      "text-[9px] font-bold uppercase rounded px-1.5 py-0.5 " +
                      (tile.isActive ? "bg-emerald-100 text-emerald-900 dark:bg-emerald-950/50 dark:text-emerald-200" : "bg-gray-200 text-gray-700 dark:bg-zinc-700 dark:text-zinc-300")
                    }
                  >
                    {tile.isActive ? "Live" : "Draft"}
                  </span>
                  <span className={`text-[9px] ${cardMutedTextClass}`}>{tile.catalogHint}</span>
                </div>
              </button>
            );
          })}
        </div>

        {openRow && openRowIndex !== null ? (
          <RoleplayScenarioEditorPanel
            row={openRow}
            index={openRowIndex}
            saving={vm.saving}
            busy={vm.busy}
            onClose={() => setOpenRowIndex(null)}
            publishRowAt={vm.publishRowAt}
            unpublishRowAt={vm.unpublishRowAt}
            saveRow={vm.saveRow}
            runSuggestQa={vm.runSuggestQa}
            deleteRow={handleDeleteRow}
            removeRowAt={handleRemoveRowAt}
            patchRowAt={vm.patchRowAt}
          />
        ) : (
          <p className={`text-xs italic px-1 ${cardMutedTextClass}`}>Select a workspace scenario above to open the editor.</p>
        )}
      </div>
    </section>
  );
}
